import axios, { AxiosProgressEvent } from 'axios';

import { Common } from '../common';
import {
  CreateValues,
  DocumentTemplate,
  DocumentTemplateWithProviders,
  UpdateValues,
  TextCommand,
} from '../models/previsit/documentTemplate';
import { DocumentWithTemplate } from '../models/previsit/document';

export class API {
  common: Common;

  constructor(common: Common) {
    this.common = common;
  }

  async findAllByFormSetAndField(
    formSetId: string,
    formFieldId: string,
  ): Promise<DocumentWithTemplate[]> {
    const url = this.common.constructPath(
      `/patient-previsit/form-sets/${formSetId}/fields/${formFieldId}/documents`,
    );
    return (await this.common.getJSON(url)).documents;
  }

  getPreviewUrl(document: DocumentWithTemplate): string {
    return this.common.constructPath(
      `/patient-previsit/documents/${document.id}/preview`,
    );
  }

  async generateSignedDocuments(args: {
    formSetId: string;
    formFieldId: string;
    documentToSignIds?: string[];
    documentNotSignedIds?: string[];
  }): Promise<{ success: boolean }> {
    const { formSetId, formFieldId, documentToSignIds, documentNotSignedIds } =
      args;
    const url = this.common.constructPath(
      `/patient-previsit/documents/${formSetId}/${formFieldId}/sign`,
    );
    return this.common.postJSON(url, {
      documentToSignIds,
      documentNotSignedIds,
    });
  }

  async getPreview(template: {
    gsPath: string;
    commands: TextCommand[];
  }): Promise<Blob> {
    const url = this.common.constructPath(
      `/previsit/document-templates/preview`,
    );
    const response = await this.common.authedFetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(template),
    });

    if (response.status === 200) {
      return response.blob();
    }

    if (response.headers.get('Content-Type') === 'application/json') {
      const json = await response.json();
      console.error(`Response had status ${response.status}`, json);
      throw new Error(json.message || 'Unsuccessful request');
    }
    const text = await response.text();
    console.error(`Response had status ${response.status}`, text);
    throw new Error('Unsuccessful request');
  }

  async findAll(
    query: { documentTemplateId?: string } = {},
  ): Promise<DocumentTemplate[]> {
    const url = this.common.constructPath(
      `/previsit/document-templates`,
      query,
    );
    const res = await this.common.getJSON(url);
    return res.documentTemplates;
  }

  async findOneById(id: string): Promise<DocumentTemplateWithProviders> {
    const url = this.common.constructPath(`/previsit/document-templates/${id}`);
    return (await this.common.getJSON(url)).documentTemplate;
  }

  async update(
    id: string,
    values: UpdateValues,
  ): Promise<DocumentTemplateWithProviders> {
    const url = this.common.constructPath(`/previsit/document-templates/${id}`);
    return (
      await this.common.putJSON(url, {
        name: values.name,
        gsPath: values.gsPath,
        category: values.category,
        commands: values.commands,
        previsitFormTemplateId: values.previsitFormTemplateId,
      })
    ).documentTemplate;
  }

  async remove(id: string): Promise<{ numDeleted: number }> {
    const url = this.common.constructPath(`/previsit/document-templates/${id}`);
    return this.common.deleteJSON(url);
  }

  async create(values: CreateValues): Promise<DocumentTemplateWithProviders> {
    const url = this.common.constructPath(`/previsit/document-templates/`);
    const added = await this.common.postJSON(url, values);
    return added.documentTemplate;
  }

  async findMaybeOneByFormTemplateId(
    formTemplateId: string,
  ): Promise<DocumentTemplate | undefined> {
    const url = this.common.constructPath(
      `/previsit/document-templates/linked-form-template/${formTemplateId}`,
    );
    const response = await this.common.getJSON(url);
    return response && response.document;
  }

  downloadURL(id: string): string {
    return this.common.constructPath(`/previsit/document-templates/${id}/file`);
  }

  async getSignedUploadURL(fileName: string): Promise<string> {
    const url = this.common.constructPath(
      `/previsit/document-templates/upload-url`,
      { fileName },
    );
    const response = await this.common.postJSON(url, {});
    return (response && response.url) || '';
  }

  async upload(
    file: File,
    signedUploadURL: string,
    onUploadProgress: (event: AxiosProgressEvent) => void,
  ): Promise<number> {
    const response = await axios.put(signedUploadURL, file, {
      onUploadProgress,
      headers: { 'Content-Type': 'application/pdf' },
    });
    return response.status;
  }
}
